import React, { useCallback, useEffect, useState, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../redux/IchthusSlice";
import {
  Warehouse,
  Search,
  ChevronDown,
  ChevronUp,
  Package,
  User,
  CheckCircle,
  FlaskConical,
  MapPin,
  BarChart3,
  RefreshCw,
  Layers,
  ArrowDownToLine,
  ArrowUpFromLine,
  Truck,
  AlertTriangle,
  X,
  History,
} from "lucide-react";
import { format } from "date-fns";
import Loader from "../../loader/Loader";
import Pagination from "../Pagination";
import { domain } from "../../../security";

const formatDate = (d) => (d ? format(new Date(d), "MMM dd, yyyy") : "—");

const fmt = (val) =>
  new Intl.NumberFormat("en-PH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(val ?? 0);

// ─── COMPACT STOCK BAR ────────────────────────────────────────────────────────
const MiniStockBar = ({ current, initial }) => {
  const pct = initial > 0 ? Math.min(100, (current / initial) * 100) : 0;
  const barColor =
    pct > 60
      ? "bg-emerald-500"
      : pct > 30
        ? "bg-amber-400"
        : pct > 0
          ? "bg-red-500"
          : "bg-slate-300";
  return (
    <div className="w-24">
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden w-full">
        <div
          className={`h-full rounded-full transition-all duration-700 ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-[9px] font-bold text-slate-400 mt-0.5 text-right uppercase tracking-wider">
        {pct.toFixed(0)}% Health
      </p>
    </div>
  );
};

// ─── MOVEMENT TIMELINE ────────────────────────────────────────────────────────
const MovementTimeline = ({ movements }) => {
  if (!movements || movements.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-slate-400 gap-2">
        <Layers size={20} className="opacity-30" />
        <p className="text-xs font-semibold">No movement history yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-0 px-2">
      {movements.map((mv, i) => {
        const isIn = mv.type === "IN";
        const isReset = mv.details?.includes("Reset");

        return (
          <div key={i} className="flex gap-4 group">
            <div className="flex flex-col items-center">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 border-2 shadow-sm ${
                  isIn
                    ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                    : isReset
                      ? "bg-rose-50 border-rose-200 text-rose-500"
                      : "bg-amber-50 border-amber-200 text-amber-500"
                }`}
              >
                {isIn ? (
                  <ArrowDownToLine size={12} />
                ) : (
                  <ArrowUpFromLine size={12} />
                )}
              </div>
              {i < movements.length - 1 && (
                <div className="w-0.5 flex-1 bg-slate-100 my-1 min-h-[16px]" />
              )}
            </div>
            <div className="flex-1 pb-3">
              <div
                className={`rounded-xl border p-3 group-hover:shadow-sm transition-shadow ${
                  isIn
                    ? "border-emerald-100 bg-emerald-50/40"
                    : isReset
                      ? "border-rose-100 bg-rose-50/40"
                      : "border-amber-100 bg-amber-50/30"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                        isIn
                          ? "bg-emerald-100 text-emerald-700"
                          : isReset
                            ? "bg-rose-100 text-rose-700"
                            : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {isIn ? "▲ IN" : isReset ? "▼ SYS. OUT" : "▼ OUT"}
                    </span>
                    <span className="text-sm font-black text-slate-800">
                      {fmt(mv.quantity)}{" "}
                      <span className="text-[10px] font-bold text-slate-400">
                        {mv.unit || "units"}
                      </span>
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-medium">
                    {formatDate(mv.date)}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px]">
                  <span
                    className={`font-bold ${isIn ? "text-indigo-600" : isReset ? "text-rose-600" : "text-amber-600"}`}
                  >
                    Ref: {mv.reference}
                  </span>
                  <span className="flex items-center gap-1 text-slate-500 font-semibold">
                    {isIn ? (
                      <CheckCircle size={10} className="text-emerald-500" />
                    ) : isReset ? (
                      <History size={10} className="text-rose-400" />
                    ) : (
                      <User size={10} className="text-slate-400" />
                    )}
                    {mv.actor}
                  </span>
                  {mv.details && (
                    <span className="flex items-center gap-1.5 px-2 py-0.5 bg-white border border-slate-200 rounded-md text-slate-600 font-bold shadow-sm">
                      {mv.details.includes("Consumed") ? (
                        <FlaskConical size={11} className="text-amber-500" />
                      ) : mv.details.includes("Transfer") ? (
                        <Truck size={11} className="text-indigo-500" />
                      ) : mv.details.includes("Reset") ? (
                        <AlertTriangle size={11} className="text-rose-500" />
                      ) : (
                        <MapPin size={11} className="text-emerald-500" />
                      )}
                      {mv.details}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const AllWarehouseStock = () => {
  const currentUserId = useSelector(selectUserID);
  const [warehouseStockData, setWarehouseStockData] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selection & Filters
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [stockFilter, setStockFilter] = useState("all");

  // Pagination & Expansion
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [activeTabs, setActiveTabs] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Reset Modal State
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [stockRes, whRes] = await Promise.all([
        axios.get(`${domain}/api/WarehouseStock/detail`),
        axios.get(`${domain}/api/Warehouses`),
      ]);
      setWarehouseStockData(stockRes.data || []);
      const whData = whRes.data || [];
      setWarehouses(whData);

      if (whData.length > 0 && !selectedWarehouseId) {
        setSelectedWarehouseId(whData[0].id.toString());
      }
    } catch {
      toast.error("Failed to load warehouse stock data.");
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedWarehouseId, stockFilter]);

  const toggleExpand = (productId) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      next.has(productId) ? next.delete(productId) : next.add(productId);
      return next;
    });
  };

  const handleTabChange = (productId, tabId) => {
    setActiveTabs((prev) => ({ ...prev, [productId]: tabId }));
  };

  // ─── RESET ACTION HANDLER ─────────────────────────────────────────────
  const handleResetWarehouse = async (actionType) => {
    if (!selectedWarehouseId) return toast.error("Select a warehouse first.");
    setResetLoading(true);
    try {
      const res = await axios.post(
        `${domain}/api/WarehouseStock/reset/${selectedWarehouseId}`,
        {
          action: actionType,
          userID: currentUserId,
        },
      );
      toast.success(res.data.message || `Warehouse reset successful.`);
      setIsResetModalOpen(false);
      fetchData(); // Refresh table entirely
    } catch (error) {
      toast.error(error.response?.data?.message || "Reset failed.");
    } finally {
      setResetLoading(false);
    }
  };

  const currentWarehouseItems = useMemo(() => {
    if (!selectedWarehouseId) return [];
    const whData = warehouseStockData.find(
      (w) => w.warehouseId.toString() === selectedWarehouseId,
    );
    return whData ? whData.stockItems || [] : [];
  }, [warehouseStockData, selectedWarehouseId]);

  const filteredItems = useMemo(() => {
    return currentWarehouseItems.filter((item) => {
      const matchSearch =
        !searchTerm.trim() ||
        (item.productName || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (item.itemCode || "").toLowerCase().includes(searchTerm.toLowerCase());

      const pct =
        item.totalReceived > 0
          ? (item.currentQuantity / item.totalReceived) * 100
          : 100;
      const matchStock =
        stockFilter === "all" ||
        (stockFilter === "critical" && pct < 10) ||
        (stockFilter === "low" && pct >= 10 && pct < 30) ||
        (stockFilter === "healthy" && pct >= 30);

      return matchSearch && matchStock;
    });
  }, [currentWarehouseItems, searchTerm, stockFilter]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPaginatedItems = filteredItems.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const selectedWarehouseName =
    warehouses.find((w) => w.id.toString() === selectedWarehouseId)
      ?.warehouseName || "Warehouse";

  return (
    <div className="min-h-screen pb-20 bg-slate-50/50">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-xl font-black text-slate-800 flex items-center gap-2">
                <Warehouse className="text-indigo-600" size={22} /> SYLO 1
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Detailed per-product analysis and movement history.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsResetModalOpen(true)}
                disabled={loading || currentWarehouseItems.length === 0}
                className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-xs font-bold px-4 py-2 rounded-lg transition-all disabled:opacity-50"
              >
                <AlertTriangle size={14} /> Reset Ledger
              </button>
              <button
                onClick={fetchData}
                className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-bold px-4 py-2 rounded-lg transition-all"
              >
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {/* ── WAREHOUSE TABS ────────────────────────────────── */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mr-2 shrink-0">
            Select Warehouse:
          </span>
          {warehouses.map((w) => (
            <button
              key={w.id}
              onClick={() => setSelectedWarehouseId(w.id.toString())}
              className={`px-4 py-2 whitespace-nowrap rounded-lg text-xs font-bold transition-all shadow-sm ${
                selectedWarehouseId === w.id.toString()
                  ? "bg-indigo-600 text-white shadow-indigo-200"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              {w.warehouseName}
            </button>
          ))}
        </div>

        {/* ── FILTERS ──────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search by product name or item code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs font-semibold border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none transition-all"
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide shrink-0">
            {[
              {
                id: "all",
                label: "All Items",
                color: "bg-slate-600 text-white",
              },
              {
                id: "critical",
                label: "⚠ Critical",
                color: "bg-red-500 text-white",
              },
              {
                id: "low",
                label: "Low Stock",
                color: "bg-amber-400 text-white",
              },
              {
                id: "healthy",
                label: "Healthy",
                color: "bg-emerald-500 text-white",
              },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setStockFilter(f.id)}
                className={`px-3 py-2 rounded-lg text-[10px] uppercase tracking-wider font-black transition-all ${
                  stockFilter === f.id
                    ? f.color + " shadow-sm"
                    : "bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── COMPACT DATA TABLE ───────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <BarChart3 size={16} className="text-indigo-500" />
              {selectedWarehouseName} Analysis
            </h2>
            <span className="text-[10px] font-bold text-slate-400 bg-white px-2 py-1 rounded border border-slate-200">
              {filteredItems.length} Products Found
            </span>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader />
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="p-16 text-center">
                <Package size={32} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-semibold text-sm">
                  No stock records match your filters in this warehouse.
                </p>
              </div>
            ) : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-400 font-black">
                    <th className="py-3 px-4 w-8 text-center"></th>
                    <th className="py-3 px-4">Product Info</th>
                    <th className="py-3 px-4 text-center">Health</th>
                    <th className="py-3 px-4 text-right">Received</th>
                    <th className="py-3 px-4 text-right">Used / Out</th>
                    <th className="py-3 px-4 text-right">Current Stock</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentPaginatedItems.map((item) => {
                    const isExpanded = expandedItems.has(item.productId);
                    const stockPct =
                      item.totalReceived > 0
                        ? Math.min(
                            100,
                            (item.currentQuantity / item.totalReceived) * 100,
                          )
                        : 0;
                    const lowStock = stockPct < 20;
                    const criticalStock = stockPct < 10 && stockPct > 0;
                    const emptyStock = item.currentQuantity <= 0;

                    const inMovements = (item.movements || []).filter(
                      (m) => m.type === "IN",
                    );
                    const outMovements = (item.movements || []).filter(
                      (m) => m.type === "OUT",
                    );
                    const currentTab = activeTabs[item.productId] || "overview";

                    return (
                      <React.Fragment key={item.productId}>
                        <tr
                          className={`transition-colors hover:bg-slate-50/50 ${isExpanded ? "bg-indigo-50/30" : ""}`}
                        >
                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => toggleExpand(item.productId)}
                              className={`p-1.5 rounded-md transition-colors ${
                                isExpanded
                                  ? "bg-indigo-100 text-indigo-600"
                                  : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                              }`}
                            >
                              {isExpanded ? (
                                <ChevronUp size={14} />
                              ) : (
                                <ChevronDown size={14} />
                              )}
                            </button>
                          </td>
                          <td className="py-3 px-4">
                            <p
                              className={`font-bold text-xs ${emptyStock ? "text-slate-400" : "text-slate-800"}`}
                            >
                              {item.productName}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 mt-0.5">
                              {item.itemCode && (
                                <span className="text-[9px] font-mono text-slate-500 bg-slate-100 px-1 rounded border border-slate-200">
                                  {item.itemCode}
                                </span>
                              )}
                              <span className="text-[9px] text-slate-400 font-semibold">
                                {item.uomName}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 align-middle">
                            <div className="flex justify-center">
                              <MiniStockBar
                                current={item.currentQuantity}
                                initial={item.totalReceived}
                              />
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right text-xs font-bold text-emerald-600">
                            {fmt(item.totalReceived)}
                          </td>
                          <td className="py-3 px-4 text-right text-xs font-bold text-rose-500">
                            {fmt(item.totalConsumed)}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span
                              className={`text-sm font-black ${emptyStock ? "text-slate-300" : criticalStock ? "text-red-600" : lowStock ? "text-amber-600" : "text-slate-800"}`}
                            >
                              {fmt(item.currentQuantity)}
                            </span>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="bg-slate-50/50 border-b border-slate-200">
                            <td colSpan={6} className="p-0">
                              <div className="px-12 py-4 border-l-2 border-indigo-500">
                                <div className="flex border-b border-slate-200 mb-4">
                                  {[
                                    {
                                      id: "overview",
                                      label: "All History",
                                      count: (item.movements || []).length,
                                    },
                                    {
                                      id: "in",
                                      label: "Stock IN",
                                      count: inMovements.length,
                                    },
                                    {
                                      id: "out",
                                      label: "Stock OUT",
                                      count: outMovements.length,
                                    },
                                  ].map((tab) => (
                                    <button
                                      key={tab.id}
                                      onClick={() =>
                                        handleTabChange(item.productId, tab.id)
                                      }
                                      className={`px-4 py-2 text-[10px] uppercase tracking-wider font-black transition-all border-b-2 ${
                                        currentTab === tab.id
                                          ? "border-indigo-500 text-indigo-700 bg-white"
                                          : "border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-100/50"
                                      }`}
                                    >
                                      {tab.label}{" "}
                                      <span className="ml-1 opacity-60">
                                        ({tab.count})
                                      </span>
                                    </button>
                                  ))}
                                </div>
                                <div className="max-h-[300px] overflow-y-auto bg-white rounded-xl border border-slate-200 p-2 shadow-sm">
                                  <MovementTimeline
                                    movements={
                                      currentTab === "in"
                                        ? inMovements
                                        : currentTab === "out"
                                          ? outMovements
                                          : item.movements || []
                                    }
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {!loading && filteredItems.length > 0 && (
            <div className="p-3 border-t border-slate-100 bg-white">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredItems.length}
                currentPage={currentPage}
                paginate={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── RESET MODAL ─────────────────────────────────────────────────────── */}
      {isResetModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                <AlertTriangle className="text-red-500" size={20} /> Reset
                Warehouse Ledger
              </h3>
              <button
                onClick={() => setIsResetModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4 text-sm text-slate-600 bg-slate-50/50">
              <p>
                You are about to reset the ledger for{" "}
                <strong className="text-slate-800">
                  {selectedWarehouseName}
                </strong>
                . A <b>System Adjustment (OUT)</b> receipt will be generated to
                clear the remaining balance in the timeline.
              </p>
              <p className="font-semibold text-rose-600">
                How would you like to handle the physical items?
              </p>

              <div className="space-y-3 pt-2">
                {/* Option 1: Return */}
                <button
                  onClick={() => handleResetWarehouse("RETURN")}
                  disabled={resetLoading}
                  className="w-full flex items-start gap-3 p-4 bg-white border border-emerald-200 hover:border-emerald-400 hover:shadow-md rounded-xl transition-all text-left group disabled:opacity-50"
                >
                  <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600 group-hover:bg-emerald-100">
                    <ArrowUpFromLine size={18} />
                  </div>
                  <div>
                    <strong className="block text-slate-800 font-bold mb-0.5">
                      Return to Main Storage
                    </strong>
                    <span className="text-xs text-slate-500">
                      Physical items will be changed from QUARANTINE back to
                      GOOD.
                    </span>
                  </div>
                </button>

                {/* Option 2: Empty/Sold */}
                <button
                  onClick={() => handleResetWarehouse("EMPTY")}
                  disabled={resetLoading}
                  className="w-full flex items-start gap-3 p-4 bg-white border border-red-200 hover:border-red-400 hover:shadow-md rounded-xl transition-all text-left group disabled:opacity-50"
                >
                  <div className="p-2 bg-red-50 rounded-lg text-red-600 group-hover:bg-red-100">
                    <Package size={18} />
                  </div>
                  <div>
                    <strong className="block text-slate-800 font-bold mb-0.5">
                      Warehouse is Empty
                    </strong>
                    <span className="text-xs text-slate-500">
                      Physical items are gone and will be changed from
                      QUARANTINE to SOLD.
                    </span>
                  </div>
                </button>
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setIsResetModalOpen(false)}
                className="px-4 py-2 font-bold text-slate-500 hover:text-slate-800 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllWarehouseStock;
